import React, { useState, useEffect } from 'react';
import { stockMovementService } from '../api/stockMovementService';
import Table from '../components/Common/Table';
import Modal from '../components/Common/Modal';
import Pagination from '../components/Common/Pagination';
import SearchBar from '../components/Common/SearchBar';
import StockMovementForm from '../components/StockMovements/StockMovementForm';
import '../components/Common/Common.css';

function StockMovements() {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);

  const fetchMovements = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        per_page: 15,
        search: searchTerm,
      };
      if (typeFilter) params.movement_type = typeFilter;

      const response = await stockMovementService.getStockMovements(params);
      setMovements(response.data);
      setLastPage(response.meta?.last_page || 1);
    } catch (error) {
      console.error('Error fetching movements:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMovements();
  }, [currentPage, typeFilter, searchTerm]);

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir annuler ce mouvement ? Cela inversera la modification du stock.')) {
      try {
        await stockMovementService.deleteStockMovement(id);
        fetchMovements();
      } catch (error) {
        console.error('Error cancelling movement:', error);
        alert('Échec de l\'annulation du mouvement');
      }
    }
  };

  const handleCreate = () => {
    setShowModal(true);
  };

  const handleFormSuccess = () => {
    setShowModal(false);
    fetchMovements();
  };

  const getTypeBadge = (type) => {
    const typeColors = {
      in: 'badge-success',
      sale: 'badge-info',
      gift: 'badge-warning',
      destruction: 'badge-danger',
    };
    return <span className={`badge ${typeColors[type]}`}>{type}</span>;
  };

  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'product', label: 'Produit', render: (val) => val?.name || 'N/A' },
    { key: 'movement_type', label: 'Type', render: getTypeBadge },
    {
      key: 'quantity',
      label: 'Quantité',
      render: (val, row) => (
        <span style={{ fontWeight: 600, color: row.movement_type === 'in' ? '#38a169' : '#e53e3e' }}>
          {row.movement_type === 'in' ? '+' : '-'}{val}
        </span>
      )
    },
    { key: 'unit_price', label: 'Prix unitaire', render: (val) => `${parseFloat(val).toFixed(2)} DH` },
    {
      key: 'total_amount',
      label: 'Total',
      render: (val, row) => `${(parseFloat(row.quantity) * parseFloat(row.unit_price)).toFixed(2)} DH`
    },
    { key: 'reference', label: 'Référence', render: (val) => val || '-' },
    { key: 'creator', label: 'Créé par', render: (val) => val?.name || 'N/A' },
    { key: 'created_at', label: 'Date', render: (val) => new Date(val).toLocaleDateString() },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>Mouvements de stock</h1>
        <button onClick={handleCreate} className="btn btn-primary">
          Ajouter un mouvement
        </button>
      </div>

      <p style={{ marginBottom: '20px', color: '#718096' }}>
        Suivez tous les mouvements de stock, y compris les entrées de stock, les ventes, les cadeaux et les destructions.
      </p>

      <div className="filters">
        <div className="filters-grid">
          <div style={{ gridColumn: 'span 2' }}>
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Rechercher par nom de produit, référence ou créateur..."
              onClear={() => setCurrentPage(1)}
            />
          </div>
          <div className="form-group">
            <label>Filtrer par type</label>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="">Tous les types</option>
              <option value="in">Entrée de stock</option>
              <option value="sale">Vente</option>
              <option value="gift">Cadeau</option>
              <option value="destruction">Destruction</option>
            </select>
          </div>
        </div>
      </div>

      <Table
        columns={columns}
        data={movements}
        loading={loading}
        onDelete={handleDelete}
        actions={true}
      />

      <Pagination
        currentPage={currentPage}
        lastPage={lastPage}
        onPageChange={setCurrentPage}
      />

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Ajouter un mouvement de stock"
        size="large"
      >
        <StockMovementForm
          onSuccess={handleFormSuccess}
          onCancel={() => setShowModal(false)}
        />
      </Modal>
    </div>
  );
}

export default StockMovements;
